/**
 * mutualFundCardSamples.js — the starter documents behind the dialog's "Insert sample" menu.
 *
 * PURE ESM, and deliberately nothing but data: JSON documents as STRINGS, ready to drop into a
 * textarea verbatim. No Vue, no import of the validator, no builder that assembles them at runtime —
 * a starter that is assembled is a starter that can drift from the payload the backend actually
 * emits, and the whole value of these is that they ARE the real thing.
 *
 * ── Three kinds of document live here, and the difference is not stylistic ───────────────────────
 * The three FUND CARDS (type8 / type9 / type10) are FIXED PAYLOADS: the admin edits those very
 * numbers and strings, nothing substitutes into them, and the test suite pins that they contain no
 * `{{ }}` and no `@repeat`. The two PICKERS (type14) are MOSTLY fixed — every word in them is a
 * literal the admin owns and translates — with ONE templated field, `list`, because the options are
 * whatever this turn's search found and no admin can type those in advance. The comparison V2 card
 * (type15) is a WHOLE TEMPLATE: every cell is bound to `mf.funds`, because a fixed payload of it could
 * never be saved as a real config. See `COMPARISON_V2_TEMPLATE`'s note.
 *
 * There are TWO pickers, not one, and they are two documents of the SAME card type: one per mode
 * (`cp`), chosen by a node key each. That is what keeps `prefix` / `join` / `submit` editable and
 * translated instead of compiled into the server — see `PICKER_SAMPLE`'s note.
 *
 * ── Where the three fund cards come from, and why they are copies ────────────────────────────────
 * They are the payloads in `tasks/2026-08-30-mutualfund-card-config-list/samples/*.json`, which are
 * themselves the shapes `MutualFundArtifactProjection` produces (type8 / type9 / type10), re-printed
 * at two-space indent so they read in a textarea. They are COPIED rather than fetched because those
 * three cards are FIXED PAYLOADS: the admin edits these very numbers and strings by hand, and no
 * template engine touches them.
 *
 * 🔴 THE FILE IS BIG (~23 KB of string literal for these three, ~56 KB more for type15's template)
 * AND THAT IS THE POINT. Trimming a sample to a "representative" subset would hand the admin a
 * document that renders half a card, and the first
 * thing they would learn is which fields we left out. Every field the renderer reads is present in
 * all three, including the per-field `*Color` / `*FontSize` hints that carry the design. (The pickers
 * are short for the opposite reason: their wire shape is EIGHT KEYS the owner fixed, and adding a
 * ninth is a contract break, not a richer sample.)
 */

/**
 * type8 — one fund's factsheet: symbol + fund name, the NAV block, the investment policy prose and
 * bullets, a stats table whose rows carry a `tone` (POSITIVE / NEGATIVE / DEFAULT), top holdings, the
 * disclaimer, and a footer link.
 */
export const FACTSHEET_SAMPLE = `{
  "type": "TAX_FUND_FACTSHEET",
  "version": 1,
  "refId": "type8",
  "data": {
    "symbol": "ONE-ELITE11RA",
    "symbolColor": "#1F2A44",
    "symbolFontSize": 14,
    "fundName": "กองทุนเปิด วัน อีลิท พอร์ต 11",
    "fundNameColor": "#1F2A44",
    "fundNameFontSize": 14,
    "iconUrl": "",
    "nav": {
      "label": "NAV ล่าสุด",
      "labelColor": "#1F2A44",
      "labelFontSize": 14,
      "value": 10.4521,
      "valueColor": "#8A8F9C",
      "valueFontSize": 14,
      "unit": "บาท",
      "change": 0.0842,
      "changeLabel": "0.08 (0.08%)",
      "caption": "(ข้อมูล ณ 8 ส.ค. 2569 เทียบวันก่อนหน้า)",
      "captionColor": "#8A8F9C",
      "captionFontSize": 14
    },
    "policyTitle": "นโยบายการลงทุน",
    "policyTitleColor": "#1F2A44",
    "policyTitleFontSize": 16,
    "policyParagraphs": [
      "กองทุนมีนโยบายลงทุนโดยเฉลี่ยในรอบปีบัญชีไม่น้อยกว่า 80% ของ NAV ในตราสารทุนและ/หรือหุ้นสามัญ โดยมีสัดส่วนหรือน้ำหนักการลงทุนใกล้เคียง (Full Replication) กับดัชนี Bloomberg Global Industry Elite 55 Index (ดัชนีอ้างอิง) ซึ่งประกอบด้วยหุ้นจาก 11 อุตสาหกรรม"
    ],
    "policyParagraphsColor": "#1F2A44",
    "policyParagraphsFontSize": 14,
    "policyBullets": [
      "กองทุนอาจลงทุนในกองทุนรวมอื่นภายใต้การบริหารของบริษัทจัดการได้ไม่เกิน 100% ของ NAV และกองทุนรวมอื่นนั้นลงทุนต่อในกองทุนที่บริษัทจัดการเดียวกันดูแลได้อีกไม่เกิน 1 ทอด ทั้งนี้ กองทุนปลายทางไม่สามารถลงทุนย้อนกลับในกองทุนต้นทางได้"
    ],
    "policyBulletsColor": "#1F2A44",
    "policyBulletsFontSize": 14,
    "stats": [
      {
        "label": "ประเภท",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "value": "ผสม (หุ้น+ตราสารหนี้)",
        "valueColor": "#8A8F9C",
        "valueFontSize": 14,
        "tone": "DEFAULT"
      },
      {
        "label": "ระดับความเสี่ยง",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "value": "5",
        "valueColor": "#8A8F9C",
        "valueFontSize": 14,
        "tone": "DEFAULT"
      },
      {
        "label": "ผลตอบแทน 3 เดือน",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "value": "+2.15%",
        "valueColor": "#8A8F9C",
        "valueFontSize": 14,
        "tone": "POSITIVE"
      },
      {
        "label": "ผลตอบแทน 1 ปี",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "value": "+6.82%",
        "valueColor": "#8A8F9C",
        "valueFontSize": 14,
        "tone": "POSITIVE"
      },
      {
        "label": "ผลตอบแทนตั้งแต่จัดตั้งกองทุน",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "value": "+5.40%",
        "valueColor": "#8A8F9C",
        "valueFontSize": 14,
        "tone": "POSITIVE"
      },
      {
        "label": "SD (1 ปี)",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "value": "12.4%",
        "valueColor": "#8A8F9C",
        "valueFontSize": 14,
        "tone": "DEFAULT"
      },
      {
        "label": "Sharpe Ratio (1 ปี)",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "value": "0.58",
        "valueColor": "#8A8F9C",
        "valueFontSize": 14,
        "tone": "DEFAULT"
      },
      {
        "label": "Max Drawdown (1 ปี)",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "value": "-9.2%",
        "valueColor": "#8A8F9C",
        "valueFontSize": 14,
        "tone": "NEGATIVE"
      }
    ],
    "statsCaption": "ข้อมูล ณ 30 มิ.ย. 2569",
    "statsCaptionColor": "#8A8F9C",
    "statsCaptionFontSize": 14,
    "holdingsTitle": "สัดส่วนของการลงทุน",
    "holdingsTitleColor": "#1F2A44",
    "holdingsTitleFontSize": 16,
    "holdings": [
      {
        "name": "Apple Inc",
        "nameColor": "#1F2A44",
        "nameFontSize": 14,
        "percent": 14.85,
        "percentColor": "#8A8F9C",
        "percentFontSize": 14
      },
      {
        "name": "NVIDIA Corp",
        "nameColor": "#1F2A44",
        "nameFontSize": 14,
        "percent": 14.68,
        "percentColor": "#8A8F9C",
        "percentFontSize": 14
      },
      {
        "name": "Microsoft Corp",
        "nameColor": "#1F2A44",
        "nameFontSize": 14,
        "percent": 14.61,
        "percentColor": "#8A8F9C",
        "percentFontSize": 14
      },
      {
        "name": "Alphabet Inc Class A",
        "nameColor": "#1F2A44",
        "nameFontSize": 14,
        "percent": 10.9,
        "percentColor": "#8A8F9C",
        "percentFontSize": 14
      },
      {
        "name": "ASML Holding NV",
        "nameColor": "#1F2A44",
        "nameFontSize": 14,
        "percent": 5.21,
        "percentColor": "#8A8F9C",
        "percentFontSize": 14
      },
      {
        "name": "อื่นๆ",
        "nameColor": "#1F2A44",
        "nameFontSize": 14,
        "percent": 39.75,
        "percentColor": "#8A8F9C",
        "percentFontSize": 14
      }
    ],
    "holdingsCaption": "ข้อมูล ณ 30 มิ.ย. 2569 อาจเปลี่ยนแปลงตามดุลยพินิจผู้จัดการกองทุน",
    "holdingsCaptionColor": "#8A8F9C",
    "holdingsCaptionFontSize": 14,
    "disclaimer": "ผลตอบแทนในอดีตไม่ได้การันตีผลตอบแทนในอนาคต",
    "disclaimerColor": "#8A8F9C",
    "disclaimerFontSize": 14,
    "footer": {
      "label": "รายละเอียดเพิ่มเติม คลิก",
      "labelColor": "#1F2A44",
      "labelFontSize": 14,
      "linkLabel": "กองทุน One-elite11-ra",
      "linkLabelColor": "#1F2A44",
      "linkLabelFontSize": 14,
      "linkUrl": "https://www.lhfund.co.th/"
    },
    "mainFrameColor": "#FFFFFF"
  }
}`;

/**
 * type9 — funds side by side: `funds[]` are the columns, `rows[]` the attributes compared. The first
 * column is frozen while the rest scrolls sideways, and `scrollHint` is the line that says so.
 */
export const COMPARISON_SAMPLE = `{
  "type": "TAX_FUND_COMPARISON",
  "version": 1,
  "refId": "type9",
  "data": {
    "title": "สามารถเปรียบเทียบผลการดำเนินงานของกองทุน ONE-ELITE11RA, KKP-GNP, SCBGOLD และ K-USA ดังนี้ 👇",
    "titleColor": "#1F2A44",
    "titleFontSize": 16,
    "tableHeight": 200,
    "rowHeaderLabel": "รายการ",
    "rowHeaderLabelColor": "#1F2A44",
    "rowHeaderLabelFontSize": 16,
    "funds": [
      "ONE-ELITE11RA",
      "KKP-GNP",
      "SCBGOLD",
      "K-USA"
    ],
    "fundsColor": "#1F2A44",
    "fundsFontSize": 14,
    "rows": [
      {
        "rowFrameColor": "#EAF1E6",
        "label": "Morningstar Rating",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "★★★★☆",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "★★★☆☆",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "★★★★★",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "★★★☆☆",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "label": "ประเภทกองทุน",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "ผสม (หุ้น+ตราสารหนี้)",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "หุ้นต่างประเทศ",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "ทองคำ",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "หุ้นต่างประเทศ",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "label": "ระดับความเสี่ยง",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "5",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "6",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "8",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "6",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "label": "ผลตอบแทน 1 ปี",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "+6.82%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "+9.10%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "+12.30%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "+15.40%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "label": "ผลตอบแทนตั้งแต่จัดตั้งกองทุน",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "+5.40%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "+7.90%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "+4.20%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "+11.10%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "label": "SD (1 ปี)",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "12.4%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "15.8%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "18.2%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "16.0%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "label": "Sharpe Ratio (1 ปี)",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "0.58",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "0.71",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "0.42",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "0.88",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "label": "Maximum Drawdown (1 ปี)",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-9.2%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-14.5%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-20.1%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-12.0%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "label": "นโยบายการจ่ายปันผล",
        "labelColor": "#1F2A44",
        "labelFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "ไม่จ่ายปันผล",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "จ่ายปันผล",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "ไม่จ่ายปันผล",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "จ่ายปันผล",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      }
    ],
    "scrollHint": "เลื่อนซ้าย-ขวาเพื่อดูกองทุนอื่น",
    "scrollHintColor": "#8A8F9C",
    "scrollHintFontSize": 14,
    "disclaimer": "ผลตอบแทนในอดีตไม่ได้การันตีผลตอบแทนในอนาคต และกองทุนทั้งสองมีระดับความเสี่ยงและสินทรัพย์ที่ลงทุนต่างกัน ควรพิจารณาให้เหมาะกับเป้าหมายของตนเอง",
    "disclaimerColor": "#8A8F9C",
    "disclaimerFontSize": 14,
    "footer": {
      "label": "ดูรายละเอียดฉบับเต็ม คลิก",
      "labelColor": "#1F2A44",
      "labelFontSize": 14,
      "separator": ", ",
      "lastSeparator": " หรือ ",
      "links": [
        {
          "rowFrameColor": "#EAF1E6",
          "label": "ONE-ELITE11RA",
          "labelColor": "#1F2A44",
          "labelFontSize": 14,
          "url": "https://www.lhfund.co.th/"
        },
        {
          "rowFrameColor": "#EAF1E6",
          "label": "KKP-GNP",
          "labelColor": "#1F2A44",
          "labelFontSize": 14,
          "url": "https://www.kkpfg.com/"
        },
        {
          "rowFrameColor": "#EAF1E6",
          "label": "SCBGOLD",
          "labelColor": "#1F2A44",
          "labelFontSize": 14,
          "url": "https://www.scbam.com/"
        },
        {
          "rowFrameColor": "#EAF1E6",
          "label": "K-USA",
          "labelColor": "#1F2A44",
          "labelFontSize": 14,
          "url": "https://www.kasikornasset.com/"
        }
      ]
    },
    "mainFrameColor": "#FFFFFF"
  }
}`;

/**
 * type10 — the performance table, in the FLAT shape: `periodHeaderLabel` + `columns[]` for the head,
 * `rows[].period` + `rows[].cells[]` for the body, `caption` underneath, `tableHeight` capping it.
 *
 * 🔴 NOT the `{ factsheet, performance }` shape written up in the tax-card-types document. That
 * describes a different envelope; this is the one the product emits and the one the owner signed off
 * on, and the renderer is built against it.
 */
export const PERFORMANCE_SAMPLE = `{
  "type": "TAX_FUND_PERFORMANCE",
  "version": 1,
  "refId": "type10",
  "data": {
    "title": "ผลตอบแทนและความเสี่ยง",
    "titleColor": "#1F2A44",
    "titleFontSize": 16,
    "tableHeight": 400,
    "periodHeaderLabel": "ช่วงเวลา",
    "periodHeaderLabelColor": "#1F2A44",
    "periodHeaderLabelFontSize": 16,
    "columns": [
      "Total Return",
      "SD",
      "Sharpe Ratio",
      "Max Drawdown"
    ],
    "columnsColor": "#1F2A44",
    "columnsFontSize": 14,
    "rows": [
      {
        "rowFrameColor": "#EAF1E6",
        "period": "YTD",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "33.96%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "period": "1M",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "2.93%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "period": "2M",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "6.89%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "period": "3M",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "31.11%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "34.75%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "period": "6M",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "46.34%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "34.28%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "period": "1Y",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "31.66%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "33.89%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "0.53",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-21.08%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "period": "3Y",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "20.56%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "24.12%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "0.60",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-22.50%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "period": "5Y",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "5.21%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      },
      {
        "rowFrameColor": "#EAF1E6",
        "period": "Since Inception",
        "periodColor": "#1F2A44",
        "periodFontSize": 14,
        "cells": [
          {
            "rowFrameColor": "#EAF1E6",
            "value": "4.95%",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          },
          {
            "rowFrameColor": "#EAF1E6",
            "value": "-",
            "valueColor": "#8A8F9C",
            "valueFontSize": 14
          }
        ]
      }
    ],
    "caption": "ข้อมูล ณ 8 ส.ค. 2569",
    "captionColor": "#8A8F9C",
    "captionFontSize": 14,
    "mainFrameColor": "#FFFFFF"
  }
}`;

/**
 * type14 — the fund PICKER, single-select: the card the customer answers when they asked ABOUT a fund
 * and the search matched more than one. The phone lists `list`, the customer taps one, and what comes
 * back is an ordinary chat message: `prefix + code`.
 *
 * 🔴 TWO STORED CONFIGS, ONE PER MODE — this one and {@link PICKER_COMPARE_SAMPLE}. The node carries a
 * code for each (`mutualFundPickerConfigCode` / `mutualFundPickerCompareConfigCode`), so each document
 * states its own mode in `cp` and nothing is decided at run time. That is what lets `prefix` and
 * `submit` be EDITABLE, TRANSLATED text rather than constants compiled into the server.
 *
 * 🔴 `join` AND `submit` ARE EMPTY ON PURPOSE, not unfinished. A single-select picker sends exactly one
 * code (nothing to join) and fires on the tap (no button to caption). An empty `submit` is how the wire
 * says "draw no button".
 *
 * 🔴 THE TRAILING SPACE IN `prefix` IS PART OF THE VALUE. The message is a plain concatenation, so
 * "ขอดูข้อมูลกองทุน " without it produces "ขอดูข้อมูลกองทุนABALL-TESGX1". It is also load-bearing in a
 * second way: the sentence comes straight back through this node's extractor, and the single-select
 * wording is what makes the next turn read `cp=false`.
 *
 * 🔴 ALL THREE LANGUAGES, EVERY SEAT. A bag missing one language sends the WHOLE CARD back to Thai for
 * that customer — the collapser is all-or-nothing per card, not per field.
 *
 * `list` is the one templated part, and has to be: the options are whatever this turn's search found.
 * `@omitWhenNull` keeps a fund with no code out of the array instead of putting a null in it that the
 * phone would draw as a blank chip.
 */
export const PICKER_SAMPLE = `{
  "type": "MF",
  "version": 1,
  "refId": "type14",
  "cp": false,
  "prefix": {
    "th": "ขอดูข้อมูลกองทุน ",
    "en": "Show fund ",
    "zh": "查看基金 "
  },
  "join": "",
  "submit": "",
  "list": {
    "@repeat": "mf.funds",
    "@template": {
      "@omitWhenNull": "$.fundCode",
      "@value": "{{ $.fundCode }}"
    }
  }
}`;

/**
 * type14 — the fund PICKER, compare: the customer asked to COMPARE but the search matched more funds
 * than they named, so this one lets them tick several and press the button. What comes back is
 * `prefix + codes.join(join)`, and the next turn — now that the funds are unambiguous — draws the
 * comparison card (type9).
 *
 * The twin of {@link PICKER_SAMPLE}; everything in that note applies here too. What differs is exactly
 * what the mode changes:
 *   • `cp: true`   — the app draws tick boxes and a button instead of single-tap chips
 *   • `join: ", "` — the separator BETWEEN the chosen codes, with its space; blank would weld them
 *   • `submit`     — the button's caption, which single-select has no use for
 *   • `prefix`     — "ขอเปรียบเทียบกองทุน ", the wording that makes the next turn read `cp=true`
 *
 * 🔴 Shipping the single-select wording here is the one edit that breaks the loop guard: the sentence
 * would re-read as `cp=false`, the node would match several funds again, and the picker would come
 * back forever instead of the comparison card.
 */
export const PICKER_COMPARE_SAMPLE = `{
  "type": "MF",
  "version": 1,
  "refId": "type14",
  "cp": true,
  "prefix": {
    "th": "ขอเปรียบเทียบกองทุน ",
    "en": "Compare funds ",
    "zh": "比较基金 "
  },
  "join": ", ",
  "submit": {
    "th": "เลือกเปรียบเทียบ",
    "en": "Compare selected",
    "zh": "比较所选"
  },
  "list": {
    "@repeat": "mf.funds",
    "@template": {
      "@omitWhenNull": "$.fundCode",
      "@value": "{{ $.fundCode }}"
    }
  }
}`;

/**
 * type15 — the comparison V2 card (4 tabs), and unlike the three fund cards above it is a TEMPLATE:
 * `Insert sample` then Save gives a config that renders live data, with nothing left to fill in.
 *
 * 🔴 DO NOT EDIT THIS TEXT HERE. It is a copy of
 * `BAL.Tests/TestData/mutualfund-templates/TAX_FUND_COMPARISON_V2.json`, the document the backend
 * tests render, and `BAL.Tests/MutualFundCompareV2SampleParityTests.cs` fails on any difference.
 * Change the fixture and copy it over whole. That test also refuses a backslash, a backtick or a
 * dollar-brace in the fixture: inside this literal JS would change or end the text, and what the
 * admin receives would no longer be what the tests rendered.
 */
export const COMPARISON_V2_TEMPLATE = `{
  "type": "TAX_FUND_COMPARISON_V2",
  "version": 1,
  "refId": "type15",
  "data": {
    "title": {
      "th": "เปรียบเทียบกองทุน",
      "en": "Fund comparison",
      "zh": "基金比较"
    },
    "selectedTab": "perf",
    "highlightRow": null,
    "bestLabel": {
      "th": "ดีที่สุด",
      "en": "Best",
      "zh": "最佳"
    },
    "funds": {
      "@repeat": "mf.funds",
      "@template": {
        "code": "{{ $.fundCode }}",
        "name": "{{ $.fund_name_en ?? $.fund_name ?? $.fundNameDisplay ?? $.fundCode }}",
        "logoText": {
          "@omitWhenNull": "$.amcInitials",
          "@value": "{{ $.amcInitials }}",
          "@else": ""
        },
        "logoUrl": null,
        "lineColor": {
          "@omitWhenNull": "$.is.position.1",
          "@value": "#1FB5A6",
          "@else": {
            "@omitWhenNull": "$.is.position.2",
            "@value": "#7B6CF6",
            "@else": "#F2A33A"
          }
        }
      }
    },
    "tabs": [
      {
        "key": "perf",
        "label": {
          "th": "ผลการดำเนินงาน",
          "en": "Performance",
          "zh": "业绩表现"
        },
        "selectedPeriod": "1Y",
        "periods": [
          {
            "key": "YTD",
            "label": "YTD",
            "note": {
              "@omitWhenNull": "mf.asOf.day_end_nav_date.th",
              "@value": {
                "th": "ข้อมูล ณ {{ mf.asOf.day_end_nav_date.th }}",
                "en": "As of {{ mf.asOf.day_end_nav_date.en }}",
                "zh": "数据截至 {{ mf.asOf.day_end_nav_date.zh }}"
              },
              "@else": ""
            },
            "rows": [
              {
                "key": "ms",
                "label": {
                  "th": "MORNINGSTAR",
                  "en": "MORNINGSTAR",
                  "zh": "MORNINGSTAR"
                },
                "labelColor": "#E5302B",
                "labelFontSize": 11,
                "labelFontWeight": "bold",
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.ratingStars",
                      "@value": "{{ $.ratingStars }}",
                      "@else": "-"
                    },
                    "valueColor": {
                      "@omitWhenNull": "$.ratingStars",
                      "@value": "#E0B33B"
                    }
                  }
                }
              },
              {
                "key": "ret",
                "label": {
                  "th": "ผลตอบแทนรวม",
                  "en": "Total return",
                  "zh": "总回报"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.return_ytd",
                      "@value": "{{ $.return_ytd | 0.00% }}",
                      "@else": "-"
                    },
                    "best": {
                      "@omitWhenNull": "$.best.return_ytd",
                      "@value": true
                    }
                  }
                }
              },
              {
                "key": "sd",
                "label": {
                  "th": "ความผันผวน (SD)",
                  "en": "SD",
                  "zh": "标准差"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": "-"
                  }
                }
              },
              {
                "key": "sharpe",
                "label": {
                  "th": "Sharpe Ratio",
                  "en": "Sharpe ratio",
                  "zh": "夏普比率"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": "-"
                  }
                }
              },
              {
                "key": "mdd",
                "label": {
                  "th": "ขาดทุนสูงสุด",
                  "en": "Maximum drawdown",
                  "zh": "最大回撤"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": "-"
                  }
                }
              },
              {
                "key": "divpol",
                "label": {
                  "th": "นโยบายปันผล",
                  "en": "Dividend policy",
                  "zh": "分红政策"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.is.dividend_flag.y",
                      "@value": {
                        "th": "จ่ายปันผล",
                        "en": "Pay",
                        "zh": "分红"
                      },
                      "@else": {
                        "@omitWhenNull": "$.is.dividend_flag.n",
                        "@value": {
                          "th": "ไม่จ่ายปันผล",
                          "en": "Not pay",
                          "zh": "不分红"
                        },
                        "@else": {
                          "@omitWhenNull": "$.dividend_flag",
                          "@value": "{{ $.dividend_flag }}",
                          "@else": "-"
                        }
                      }
                    }
                  }
                }
              },
              {
                "key": "lastdiv",
                "label": {
                  "th": "ปันผลล่าสุด (บาท/หน่วย)",
                  "en": "Latest dividend (THB/unit)",
                  "zh": "最近分红（泰铢/单位）"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.dividend",
                      "@value": "{{ $.dividend | 0.00## }}",
                      "@else": "-"
                    }
                  }
                }
              }
            ]
          },
          {
            "key": "1W",
            "label": "1W",
            "note": {
              "@omitWhenNull": "mf.asOf.day_end_nav_date.th",
              "@value": {
                "th": "ข้อมูล ณ {{ mf.asOf.day_end_nav_date.th }}",
                "en": "As of {{ mf.asOf.day_end_nav_date.en }}",
                "zh": "数据截至 {{ mf.asOf.day_end_nav_date.zh }}"
              },
              "@else": ""
            },
            "rows": [
              {
                "key": "ms",
                "label": {
                  "th": "MORNINGSTAR",
                  "en": "MORNINGSTAR",
                  "zh": "MORNINGSTAR"
                },
                "labelColor": "#E5302B",
                "labelFontSize": 11,
                "labelFontWeight": "bold",
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.ratingStars",
                      "@value": "{{ $.ratingStars }}",
                      "@else": "-"
                    },
                    "valueColor": {
                      "@omitWhenNull": "$.ratingStars",
                      "@value": "#E0B33B"
                    }
                  }
                }
              },
              {
                "key": "ret",
                "label": {
                  "th": "ผลตอบแทนรวม",
                  "en": "Total return",
                  "zh": "总回报"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.return_1week",
                      "@value": "{{ $.return_1week | 0.00% }}",
                      "@else": "-"
                    },
                    "best": {
                      "@omitWhenNull": "$.best.return_1week",
                      "@value": true
                    }
                  }
                }
              },
              {
                "key": "sd",
                "label": {
                  "th": "ความผันผวน (SD)",
                  "en": "SD",
                  "zh": "标准差"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": "-"
                  }
                }
              },
              {
                "key": "sharpe",
                "label": {
                  "th": "Sharpe Ratio",
                  "en": "Sharpe ratio",
                  "zh": "夏普比率"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": "-"
                  }
                }
              },
              {
                "key": "mdd",
                "label": {
                  "th": "ขาดทุนสูงสุด",
                  "en": "Maximum drawdown",
                  "zh": "最大回撤"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": "-"
                  }
                }
              },
              {
                "key": "divpol",
                "label": {
                  "th": "นโยบายปันผล",
                  "en": "Dividend policy",
                  "zh": "分红政策"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.is.dividend_flag.y",
                      "@value": {
                        "th": "จ่ายปันผล",
                        "en": "Pay",
                        "zh": "分红"
                      },
                      "@else": {
                        "@omitWhenNull": "$.is.dividend_flag.n",
                        "@value": {
                          "th": "ไม่จ่ายปันผล",
                          "en": "Not pay",
                          "zh": "不分红"
                        },
                        "@else": {
                          "@omitWhenNull": "$.dividend_flag",
                          "@value": "{{ $.dividend_flag }}",
                          "@else": "-"
                        }
                      }
                    }
                  }
                }
              },
              {
                "key": "lastdiv",
                "label": {
                  "th": "ปันผลล่าสุด (บาท/หน่วย)",
                  "en": "Latest dividend (THB/unit)",
                  "zh": "最近分红（泰铢/单位）"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.dividend",
                      "@value": "{{ $.dividend | 0.00## }}",
                      "@else": "-"
                    }
                  }
                }
              }
            ]
          },
          {
            "key": "1M",
            "label": "1M",
            "note": {
              "@omitWhenNull": "mf.asOf.day_end_nav_date.th",
              "@value": {
                "th": "ข้อมูล ณ {{ mf.asOf.day_end_nav_date.th }}",
                "en": "As of {{ mf.asOf.day_end_nav_date.en }}",
                "zh": "数据截至 {{ mf.asOf.day_end_nav_date.zh }}"
              },
              "@else": ""
            },
            "rows": [
              {
                "key": "ms",
                "label": {
                  "th": "MORNINGSTAR",
                  "en": "MORNINGSTAR",
                  "zh": "MORNINGSTAR"
                },
                "labelColor": "#E5302B",
                "labelFontSize": 11,
                "labelFontWeight": "bold",
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.ratingStars",
                      "@value": "{{ $.ratingStars }}",
                      "@else": "-"
                    },
                    "valueColor": {
                      "@omitWhenNull": "$.ratingStars",
                      "@value": "#E0B33B"
                    }
                  }
                }
              },
              {
                "key": "ret",
                "label": {
                  "th": "ผลตอบแทนรวม",
                  "en": "Total return",
                  "zh": "总回报"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.return_1mth",
                      "@value": "{{ $.return_1mth | 0.00% }}",
                      "@else": "-"
                    },
                    "best": {
                      "@omitWhenNull": "$.best.return_1mth",
                      "@value": true
                    }
                  }
                }
              },
              {
                "key": "sd",
                "label": {
                  "th": "ความผันผวน (SD)",
                  "en": "SD",
                  "zh": "标准差"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": "-"
                  }
                }
              },
              {
                "key": "sharpe",
                "label": {
                  "th": "Sharpe Ratio",
                  "en": "Sharpe ratio",
                  "zh": "夏普比率"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": "-"
                  }
                }
              },
              {
                "key": "mdd",
                "label": {
                  "th": "ขาดทุนสูงสุด",
                  "en": "Maximum drawdown",
                  "zh": "最大回撤"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": "-"
                  }
                }
              },
              {
                "key": "divpol",
                "label": {
                  "th": "นโยบายปันผล",
                  "en": "Dividend policy",
                  "zh": "分红政策"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.is.dividend_flag.y",
                      "@value": {
                        "th": "จ่ายปันผล",
                        "en": "Pay",
                        "zh": "分红"
                      },
                      "@else": {
                        "@omitWhenNull": "$.is.dividend_flag.n",
                        "@value": {
                          "th": "ไม่จ่ายปันผล",
                          "en": "Not pay",
                          "zh": "不分红"
                        },
                        "@else": {
                          "@omitWhenNull": "$.dividend_flag",
                          "@value": "{{ $.dividend_flag }}",
                          "@else": "-"
                        }
                      }
                    }
                  }
                }
              },
              {
                "key": "lastdiv",
                "label": {
                  "th": "ปันผลล่าสุด (บาท/หน่วย)",
                  "en": "Latest dividend (THB/unit)",
                  "zh": "最近分红（泰铢/单位）"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.dividend",
                      "@value": "{{ $.dividend | 0.00## }}",
                      "@else": "-"
                    }
                  }
                }
              }
            ]
          },
          {
            "key": "6M",
            "label": "6M",
            "note": {
              "@omitWhenNull": "mf.asOf.day_end_nav_date.th",
              "@value": {
                "th": "ข้อมูล ณ {{ mf.asOf.day_end_nav_date.th }}",
                "en": "As of {{ mf.asOf.day_end_nav_date.en }}",
                "zh": "数据截至 {{ mf.asOf.day_end_nav_date.zh }}"
              },
              "@else": ""
            },
            "rows": [
              {
                "key": "ms",
                "label": {
                  "th": "MORNINGSTAR",
                  "en": "MORNINGSTAR",
                  "zh": "MORNINGSTAR"
                },
                "labelColor": "#E5302B",
                "labelFontSize": 11,
                "labelFontWeight": "bold",
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.ratingStars",
                      "@value": "{{ $.ratingStars }}",
                      "@else": "-"
                    },
                    "valueColor": {
                      "@omitWhenNull": "$.ratingStars",
                      "@value": "#E0B33B"
                    }
                  }
                }
              },
              {
                "key": "ret",
                "label": {
                  "th": "ผลตอบแทนรวม",
                  "en": "Total return",
                  "zh": "总回报"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.return_6mth",
                      "@value": "{{ $.return_6mth | 0.00% }}",
                      "@else": "-"
                    },
                    "best": {
                      "@omitWhenNull": "$.best.return_6mth",
                      "@value": true
                    }
                  }
                }
              },
              {
                "key": "sd",
                "label": {
                  "th": "ความผันผวน (SD)",
                  "en": "SD",
                  "zh": "标准差"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.std_dev_6mo",
                      "@value": "{{ $.std_dev_6mo | 0.00% }}",
                      "@else": "-"
                    }
                  }
                }
              },
              {
                "key": "sharpe",
                "label": {
                  "th": "Sharpe Ratio",
                  "en": "Sharpe ratio",
                  "zh": "夏普比率"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": "-"
                  }
                }
              },
              {
                "key": "mdd",
                "label": {
                  "th": "ขาดทุนสูงสุด",
                  "en": "Maximum drawdown",
                  "zh": "最大回撤"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": "-"
                  }
                }
              },
              {
                "key": "divpol",
                "label": {
                  "th": "นโยบายปันผล",
                  "en": "Dividend policy",
                  "zh": "分红政策"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.is.dividend_flag.y",
                      "@value": {
                        "th": "จ่ายปันผล",
                        "en": "Pay",
                        "zh": "分红"
                      },
                      "@else": {
                        "@omitWhenNull": "$.is.dividend_flag.n",
                        "@value": {
                          "th": "ไม่จ่ายปันผล",
                          "en": "Not pay",
                          "zh": "不分红"
                        },
                        "@else": {
                          "@omitWhenNull": "$.dividend_flag",
                          "@value": "{{ $.dividend_flag }}",
                          "@else": "-"
                        }
                      }
                    }
                  }
                }
              },
              {
                "key": "lastdiv",
                "label": {
                  "th": "ปันผลล่าสุด (บาท/หน่วย)",
                  "en": "Latest dividend (THB/unit)",
                  "zh": "最近分红（泰铢/单位）"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.dividend",
                      "@value": "{{ $.dividend | 0.00## }}",
                      "@else": "-"
                    }
                  }
                }
              }
            ]
          },
          {
            "key": "1Y",
            "label": "1Y",
            "note": {
              "@omitWhenNull": "mf.asOf.day_end_nav_date.th",
              "@value": {
                "th": "ข้อมูล ณ {{ mf.asOf.day_end_nav_date.th }}",
                "en": "As of {{ mf.asOf.day_end_nav_date.en }}",
                "zh": "数据截至 {{ mf.asOf.day_end_nav_date.zh }}"
              },
              "@else": ""
            },
            "rows": [
              {
                "key": "ms",
                "label": {
                  "th": "MORNINGSTAR",
                  "en": "MORNINGSTAR",
                  "zh": "MORNINGSTAR"
                },
                "labelColor": "#E5302B",
                "labelFontSize": 11,
                "labelFontWeight": "bold",
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.ratingStars",
                      "@value": "{{ $.ratingStars }}",
                      "@else": "-"
                    },
                    "valueColor": {
                      "@omitWhenNull": "$.ratingStars",
                      "@value": "#E0B33B"
                    }
                  }
                }
              },
              {
                "key": "ret",
                "label": {
                  "th": "ผลตอบแทนรวม",
                  "en": "Total return",
                  "zh": "总回报"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.return_1yr",
                      "@value": "{{ $.return_1yr | 0.00% }}",
                      "@else": "-"
                    },
                    "best": {
                      "@omitWhenNull": "$.best.return_1yr",
                      "@value": true
                    }
                  }
                }
              },
              {
                "key": "sd",
                "label": {
                  "th": "ความผันผวน (SD)",
                  "en": "SD",
                  "zh": "标准差"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.std_dev_1yr",
                      "@value": "{{ $.std_dev_1yr | 0.00% }}",
                      "@else": "-"
                    }
                  }
                }
              },
              {
                "key": "sharpe",
                "label": {
                  "th": "Sharpe Ratio",
                  "en": "Sharpe ratio",
                  "zh": "夏普比率"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.sharpe_ratio_1yr",
                      "@value": "{{ $.sharpe_ratio_1yr | 0.00 }}",
                      "@else": "-"
                    },
                    "best": {
                      "@omitWhenNull": "$.best.sharpe_ratio_1yr",
                      "@value": true
                    }
                  }
                }
              },
              {
                "key": "mdd",
                "label": {
                  "th": "ขาดทุนสูงสุด",
                  "en": "Maximum drawdown",
                  "zh": "最大回撤"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.max_drawdown_1yr",
                      "@value": "{{ $.max_drawdown_1yr | 0.00% }}",
                      "@else": "-"
                    },
                    "best": {
                      "@omitWhenNull": "$.best.max_drawdown_1yr",
                      "@value": true
                    }
                  }
                }
              },
              {
                "key": "divpol",
                "label": {
                  "th": "นโยบายปันผล",
                  "en": "Dividend policy",
                  "zh": "分红政策"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.is.dividend_flag.y",
                      "@value": {
                        "th": "จ่ายปันผล",
                        "en": "Pay",
                        "zh": "分红"
                      },
                      "@else": {
                        "@omitWhenNull": "$.is.dividend_flag.n",
                        "@value": {
                          "th": "ไม่จ่ายปันผล",
                          "en": "Not pay",
                          "zh": "不分红"
                        },
                        "@else": {
                          "@omitWhenNull": "$.dividend_flag",
                          "@value": "{{ $.dividend_flag }}",
                          "@else": "-"
                        }
                      }
                    }
                  }
                }
              },
              {
                "key": "lastdiv",
                "label": {
                  "th": "ปันผลล่าสุด (บาท/หน่วย)",
                  "en": "Latest dividend (THB/unit)",
                  "zh": "最近分红（泰铢/单位）"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.dividend",
                      "@value": "{{ $.dividend | 0.00## }}",
                      "@else": "-"
                    }
                  }
                }
              }
            ]
          },
          {
            "key": "3Y",
            "label": "3Y",
            "note": {
              "@omitWhenNull": "mf.asOf.day_end_nav_date.th",
              "@value": {
                "th": "ข้อมูล ณ {{ mf.asOf.day_end_nav_date.th }}",
                "en": "As of {{ mf.asOf.day_end_nav_date.en }}",
                "zh": "数据截至 {{ mf.asOf.day_end_nav_date.zh }}"
              },
              "@else": ""
            },
            "rows": [
              {
                "key": "ms",
                "label": {
                  "th": "MORNINGSTAR",
                  "en": "MORNINGSTAR",
                  "zh": "MORNINGSTAR"
                },
                "labelColor": "#E5302B",
                "labelFontSize": 11,
                "labelFontWeight": "bold",
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.ratingStars",
                      "@value": "{{ $.ratingStars }}",
                      "@else": "-"
                    },
                    "valueColor": {
                      "@omitWhenNull": "$.ratingStars",
                      "@value": "#E0B33B"
                    }
                  }
                }
              },
              {
                "key": "ret",
                "label": {
                  "th": "ผลตอบแทนรวม",
                  "en": "Total return",
                  "zh": "总回报"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.return_3yr",
                      "@value": "{{ $.return_3yr | 0.00% }}",
                      "@else": "-"
                    },
                    "best": {
                      "@omitWhenNull": "$.best.return_3yr",
                      "@value": true
                    }
                  }
                }
              },
              {
                "key": "sd",
                "label": {
                  "th": "ความผันผวน (SD)",
                  "en": "SD",
                  "zh": "标准差"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.std_dev_3yr",
                      "@value": "{{ $.std_dev_3yr | 0.00% }}",
                      "@else": "-"
                    }
                  }
                }
              },
              {
                "key": "sharpe",
                "label": {
                  "th": "Sharpe Ratio",
                  "en": "Sharpe ratio",
                  "zh": "夏普比率"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.sharpe_ratio_3yr",
                      "@value": "{{ $.sharpe_ratio_3yr | 0.00 }}",
                      "@else": "-"
                    },
                    "best": {
                      "@omitWhenNull": "$.best.sharpe_ratio_3yr",
                      "@value": true
                    }
                  }
                }
              },
              {
                "key": "mdd",
                "label": {
                  "th": "ขาดทุนสูงสุด",
                  "en": "Maximum drawdown",
                  "zh": "最大回撤"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.max_drawdown_3yr",
                      "@value": "{{ $.max_drawdown_3yr | 0.00% }}",
                      "@else": "-"
                    },
                    "best": {
                      "@omitWhenNull": "$.best.max_drawdown_3yr",
                      "@value": true
                    }
                  }
                }
              },
              {
                "key": "divpol",
                "label": {
                  "th": "นโยบายปันผล",
                  "en": "Dividend policy",
                  "zh": "分红政策"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.is.dividend_flag.y",
                      "@value": {
                        "th": "จ่ายปันผล",
                        "en": "Pay",
                        "zh": "分红"
                      },
                      "@else": {
                        "@omitWhenNull": "$.is.dividend_flag.n",
                        "@value": {
                          "th": "ไม่จ่ายปันผล",
                          "en": "Not pay",
                          "zh": "不分红"
                        },
                        "@else": {
                          "@omitWhenNull": "$.dividend_flag",
                          "@value": "{{ $.dividend_flag }}",
                          "@else": "-"
                        }
                      }
                    }
                  }
                }
              },
              {
                "key": "lastdiv",
                "label": {
                  "th": "ปันผลล่าสุด (บาท/หน่วย)",
                  "en": "Latest dividend (THB/unit)",
                  "zh": "最近分红（泰铢/单位）"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.dividend",
                      "@value": "{{ $.dividend | 0.00## }}",
                      "@else": "-"
                    }
                  }
                }
              }
            ]
          },
          {
            "key": "5Y",
            "label": "5Y",
            "note": {
              "@omitWhenNull": "mf.asOf.day_end_nav_date.th",
              "@value": {
                "th": "ข้อมูล ณ {{ mf.asOf.day_end_nav_date.th }}",
                "en": "As of {{ mf.asOf.day_end_nav_date.en }}",
                "zh": "数据截至 {{ mf.asOf.day_end_nav_date.zh }}"
              },
              "@else": ""
            },
            "rows": [
              {
                "key": "ms",
                "label": {
                  "th": "MORNINGSTAR",
                  "en": "MORNINGSTAR",
                  "zh": "MORNINGSTAR"
                },
                "labelColor": "#E5302B",
                "labelFontSize": 11,
                "labelFontWeight": "bold",
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.ratingStars",
                      "@value": "{{ $.ratingStars }}",
                      "@else": "-"
                    },
                    "valueColor": {
                      "@omitWhenNull": "$.ratingStars",
                      "@value": "#E0B33B"
                    }
                  }
                }
              },
              {
                "key": "ret",
                "label": {
                  "th": "ผลตอบแทนรวม",
                  "en": "Total return",
                  "zh": "总回报"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.return_5yr",
                      "@value": "{{ $.return_5yr | 0.00% }}",
                      "@else": "-"
                    },
                    "best": {
                      "@omitWhenNull": "$.best.return_5yr",
                      "@value": true
                    }
                  }
                }
              },
              {
                "key": "sd",
                "label": {
                  "th": "ความผันผวน (SD)",
                  "en": "SD",
                  "zh": "标准差"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.std_dev_5yr",
                      "@value": "{{ $.std_dev_5yr | 0.00% }}",
                      "@else": "-"
                    }
                  }
                }
              },
              {
                "key": "sharpe",
                "label": {
                  "th": "Sharpe Ratio",
                  "en": "Sharpe ratio",
                  "zh": "夏普比率"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.sharpe_ratio_5yr",
                      "@value": "{{ $.sharpe_ratio_5yr | 0.00 }}",
                      "@else": "-"
                    },
                    "best": {
                      "@omitWhenNull": "$.best.sharpe_ratio_5yr",
                      "@value": true
                    }
                  }
                }
              },
              {
                "key": "mdd",
                "label": {
                  "th": "ขาดทุนสูงสุด",
                  "en": "Maximum drawdown",
                  "zh": "最大回撤"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.max_drawdown_5yr",
                      "@value": "{{ $.max_drawdown_5yr | 0.00% }}",
                      "@else": "-"
                    },
                    "best": {
                      "@omitWhenNull": "$.best.max_drawdown_5yr",
                      "@value": true
                    }
                  }
                }
              },
              {
                "key": "divpol",
                "label": {
                  "th": "นโยบายปันผล",
                  "en": "Dividend policy",
                  "zh": "分红政策"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.is.dividend_flag.y",
                      "@value": {
                        "th": "จ่ายปันผล",
                        "en": "Pay",
                        "zh": "分红"
                      },
                      "@else": {
                        "@omitWhenNull": "$.is.dividend_flag.n",
                        "@value": {
                          "th": "ไม่จ่ายปันผล",
                          "en": "Not pay",
                          "zh": "不分红"
                        },
                        "@else": {
                          "@omitWhenNull": "$.dividend_flag",
                          "@value": "{{ $.dividend_flag }}",
                          "@else": "-"
                        }
                      }
                    }
                  }
                }
              },
              {
                "key": "lastdiv",
                "label": {
                  "th": "ปันผลล่าสุด (บาท/หน่วย)",
                  "en": "Latest dividend (THB/unit)",
                  "zh": "最近分红（泰铢/单位）"
                },
                "cells": {
                  "@repeat": "mf.funds",
                  "@template": {
                    "value": {
                      "@omitWhenNull": "$.fmt.dividend",
                      "@value": "{{ $.dividend | 0.00## }}",
                      "@else": "-"
                    }
                  }
                }
              }
            ]
          }
        ]
      },
      {
        "key": "strat",
        "label": {
          "th": "นโยบายการลงทุน",
          "en": "Investment strategy",
          "zh": "投资策略"
        },
        "rows": [
          {
            "key": "amc",
            "label": {
              "th": "บลจ.",
              "en": "AMC",
              "zh": "基金公司"
            },
            "cells": {
              "@repeat": "mf.funds",
              "@template": {
                "value": {
                  "@omitWhenNull": "$.is.amc_code.lhfund",
                  "@value": "LH Fund",
                  "@else": {
                    "@omitWhenNull": "$.amc_code",
                    "@value": "{{ $.amc_code }}",
                    "@else": "-"
                  }
                }
              }
            }
          },
          {
            "key": "asset",
            "label": {
              "th": "ประเภทสินทรัพย์",
              "en": "Asset class",
              "zh": "资产类别"
            },
            "cells": {
              "@repeat": "mf.funds",
              "@template": {
                "value": {
                  "@omitWhenNull": "$.is.fif_flag.y",
                  "@value": {
                    "th": "กองทุนต่างประเทศ",
                    "en": "Foreign funds",
                    "zh": "海外基金"
                  },
                  "@else": {
                    "@omitWhenNull": "$.is.fif_flag.n",
                    "@value": {
                      "th": "กองทุนในประเทศ",
                      "en": "Domestic funds",
                      "zh": "国内基金"
                    },
                    "@else": {
                      "@omitWhenNull": "$.fif_flag",
                      "@value": "{{ $.fif_flag }}",
                      "@else": "-"
                    }
                  }
                }
              }
            }
          },
          {
            "key": "risk",
            "label": {
              "th": "ระดับความเสี่ยง",
              "en": "Risk level",
              "zh": "风险等级"
            },
            "cells": {
              "@repeat": "mf.funds",
              "@template": {
                "value": {
                  "@omitWhenNull": "$.fund_risk_level",
                  "@value": "{{ $.fund_risk_level }}",
                  "@else": "-"
                }
              }
            }
          },
          {
            "key": "strat",
            "label": {
              "th": "กลยุทธ์การลงทุน",
              "en": "Investment strategy",
              "zh": "投资策略"
            },
            "cells": {
              "@repeat": "mf.funds",
              "@template": {
                "value": {
                  "@omitWhenNull": "$.is.index_fund.true",
                  "@value": {
                    "th": "Passive",
                    "en": "Passive",
                    "zh": "被动"
                  },
                  "@else": {
                    "@omitWhenNull": "$.is.index_fund.false",
                    "@value": {
                      "th": "Active",
                      "en": "Active",
                      "zh": "主动"
                    },
                    "@else": "-"
                  }
                }
              }
            }
          },
          {
            "key": "fpol",
            "label": {
              "th": "ลงทุนต่างประเทศ",
              "en": "Foreign investment",
              "zh": "海外投资"
            },
            "cells": {
              "@repeat": "mf.funds",
              "@template": {
                "value": {
                  "@omitWhenNull": "$.is.fif_flag.y",
                  "@value": {
                    "th": "มี",
                    "en": "Yes",
                    "zh": "是"
                  },
                  "@else": {
                    "@omitWhenNull": "$.is.fif_flag.n",
                    "@value": {
                      "th": "ไม่มี",
                      "en": "No",
                      "zh": "否"
                    },
                    "@else": {
                      "@omitWhenNull": "$.fif_flag",
                      "@value": "{{ $.fif_flag }}",
                      "@else": "-"
                    }
                  }
                }
              }
            }
          },
          {
            "key": "master",
            "label": {
              "th": "กองทุนหลัก",
              "en": "Master fund",
              "zh": "主基金"
            },
            "cells": {
              "@repeat": "mf.funds",
              "@template": {
                "value": {
                  "@omitWhenNull": "$.master_fund_name",
                  "@value": "{{ $.master_fund_name }}",
                  "@else": "-"
                }
              }
            }
          },
          {
            "key": "fx",
            "label": {
              "th": "ป้องกันความเสี่ยงค่าเงิน",
              "en": "FX hedging",
              "zh": "汇率对冲"
            },
            "cells": {
              "@repeat": "mf.funds",
              "@template": {
                "value": {
                  "@omitWhenNull": "$.is.exchange_rate_risk_spectrum.fund_manager_s_discretion",
                  "@value": {
                    "th": "ดุลยพินิจผู้จัดการกองทุน",
                    "en": "Fund manager's discretion",
                    "zh": "基金经理酌情"
                  },
                  "@else": {
                    "@omitWhenNull": "$.is.exchange_rate_risk_spectrum.full_fx_risk_hedge",
                    "@value": {
                      "th": "ป้องกันความเสี่ยงทั้งหมด",
                      "en": "Fully hedged",
                      "zh": "全额对冲"
                    },
                    "@else": {
                      "@omitWhenNull": "$.is.exchange_rate_risk_spectrum.partial_fx_risk_hedge",
                      "@value": {
                        "th": "ป้องกันความเสี่ยงบางส่วน",
                        "en": "Partially hedged",
                        "zh": "部分对冲"
                      },
                      "@else": {
                        "@omitWhenNull": "$.is.exchange_rate_risk_spectrum.no_fx_risk_hedge",
                        "@value": {
                          "th": "ไม่ป้องกันความเสี่ยง",
                          "en": "Not hedged",
                          "zh": "不对冲"
                        },
                        "@else": {
                          "@omitWhenNull": "$.exchange_rate_risk_spectrum",
                          "@value": "{{ $.exchange_rate_risk_spectrum }}",
                          "@else": "-"
                        }
                      }
                    }
                  }
                }
              }
            }
          },
          {
            "key": "navm",
            "label": {
              "th": "มูลค่าทรัพย์สินสุทธิ (ล้านบาท)",
              "en": "NAV (million THB)",
              "zh": "净资产（百万泰铢）"
            },
            "cells": {
              "@repeat": "mf.funds",
              "@template": {
                "value": {
                  "@omitWhenNull": "$.aumMillion",
                  "@value": "{{ $.aumMillion | #,##0.00 }}",
                  "@else": "-"
                }
              }
            }
          }
        ]
      },
      {
        "key": "trade",
        "label": {
          "th": "ข้อมูลการซื้อขาย",
          "en": "Trading information",
          "zh": "交易信息"
        },
        "rows": [
          {
            "key": "front",
            "label": {
              "th": "ค่าธรรมเนียมขาเข้า",
              "en": "Front-end fee",
              "zh": "申购费"
            },
            "cells": {
              "@repeat": "mf.funds",
              "@template": {
                "value": {
                  "@omitWhenNull": "$.fmt.actual_front_load",
                  "@value": "{{ $.actual_front_load | 0.00% }}",
                  "@else": "-"
                }
              }
            }
          },
          {
            "key": "back",
            "label": {
              "th": "ค่าธรรมเนียมขาออก",
              "en": "Back-end fee",
              "zh": "赎回费"
            },
            "cells": {
              "@repeat": "mf.funds",
              "@template": {
                "value": {
                  "@omitWhenNull": "$.fmt.actual_defer_load",
                  "@value": "{{ $.actual_defer_load | 0.00% }}",
                  "@else": "-"
                }
              }
            }
          },
          {
            "key": "ter",
            "label": {
              "th": "Total Expense Ratio",
              "en": "Total expense ratio",
              "zh": "总费用率"
            },
            "cells": {
              "@repeat": "mf.funds",
              "@template": {
                "value": {
                  "@omitWhenNull": "$.fmt.net_expense_ratio",
                  "@value": "{{ $.net_expense_ratio | 0.00% }}",
                  "@else": "-"
                }
              }
            }
          },
          {
            "key": "first",
            "label": {
              "th": "ซื้อครั้งแรกขั้นต่ำ (บาท)",
              "en": "First minimum buy (THB)",
              "zh": "首次最低申购（泰铢）"
            },
            "cells": {
              "@repeat": "mf.funds",
              "@template": {
                "value": {
                  "@omitWhenNull": "$.fmt.fst_lowbuy_val",
                  "@value": "{{ $.fst_lowbuy_val | #,##0.00 }}",
                  "@else": "-"
                }
              }
            }
          },
          {
            "key": "next",
            "label": {
              "th": "ซื้อครั้งถัดไปขั้นต่ำ (บาท)",
              "en": "Next minimum buy (THB)",
              "zh": "追加最低申购（泰铢）"
            },
            "cells": {
              "@repeat": "mf.funds",
              "@template": {
                "value": {
                  "@omitWhenNull": "$.fmt.nxt_lowbuy_val",
                  "@value": "{{ $.nxt_lowbuy_val | #,##0.00 }}",
                  "@else": "-"
                }
              }
            }
          },
          {
            "key": "asof",
            "label": {
              "th": "ข้อมูล ณ",
              "en": "Info as of",
              "zh": "数据截至"
            },
            "cells": {
              "@repeat": "mf.funds",
              "@template": {
                "value": {
                  "@omitWhenNull": "$.dates.portfolio_date.en",
                  "@value": {
                    "th": "{{ $.dates.portfolio_date.en }}",
                    "en": "{{ $.dates.portfolio_date.en }}",
                    "zh": "{{ $.dates.portfolio_date.zh }}"
                  },
                  "@else": "-"
                }
              }
            }
          }
        ]
      },
      {
        "key": "chart",
        "label": {
          "th": "กราฟ",
          "en": "Chart",
          "zh": "图表"
        },
        "selectedPeriod": "5Y",
        "periods": [
          {
            "key": "5Y",
            "label": "5Y",
            "count": 5,
            "note": {
              "th": "ผลตอบแทนรายปีรวมเงินปันผล",
              "en": "Annual total return incl. dividends",
              "zh": "年度总回报（含分红）"
            }
          },
          {
            "key": "10Y",
            "label": "10Y",
            "count": 10,
            "note": {
              "th": "ผลตอบแทนรายปีรวมเงินปันผล",
              "en": "Annual total return incl. dividends",
              "zh": "年度总回报（含分红）"
            }
          }
        ],
        "chart": {
          "title": {
            "th": "ผลตอบแทนรวมเงินปันผลรายปี",
            "en": "Annual total return",
            "zh": "年度总回报"
          },
          "legendTitle": {
            "th": "ผลตอบแทนรวมเงินปันผล ปี {label}",
            "en": "Total return {label}",
            "zh": "{label}年总回报"
          },
          "valueSuffix": "%",
          "valueDecimals": 2,
          "valueShowsPlus": true,
          "axisDecimals": 1,
          "points": {
            "@repeat": "mf.chart.points",
            "@template": {
              "label": "{{ $.label }}",
              "values": "{{ $.values }}"
            }
          }
        }
      }
    ],
    "disclaimer": {
      "th": "ผลตอบแทนในอดีตไม่ได้การันตีผลตอบแทนในอนาคต",
      "en": "Past performance does not guarantee future returns.",
      "zh": "过往业绩不代表未来回报。"
    },
    "button": {
      "label": {
        "th": "ซื้อ",
        "en": "Buy",
        "zh": "购买"
      }
    },
    "mainFrameColor": "#FFFFFF",
    "mainBorderColor": "#E1E4EE",
    "mainBorderWidth": 1,
    "mainCornerRadius": 18,
    "mainPaddingTop": 14,
    "mainPaddingBottom": 16,
    "mainPaddingLeft": 14,
    "mainPaddingRight": 14,
    "mainMarginTop": 8,
    "mainMarginBottom": 8,
    "mainSpacing": 10,
    "mainGradientStartColor": "#E9F6EE",
    "mainGradientEndColor": "#FFFFFF",
    "mainGradientHeight": 96,
    "titleColor": "#1C1E33",
    "titleFontSize": 17,
    "titleFontWeight": "bold",
    "titleTextAlign": "left",
    "tabFrameColor": "#FFFFFF",
    "tabBorderColor": "#E1E4EE",
    "tabBorderWidth": 1,
    "tabCornerRadius": 10,
    "tabHeight": 36,
    "tabSpacing": 6,
    "tabPaddingLeft": 14,
    "tabPaddingRight": 14,
    "tabColor": "#1C1E33",
    "tabFontSize": 11.5,
    "tabFontWeight": "semibold",
    "tabSelectedFrameColor": "#23295A",
    "tabSelectedBorderColor": "#23295A",
    "tabSelectedColor": "#FFFFFF",
    "periodFrameColor": "#F1F3F8",
    "periodCornerRadius": 13,
    "periodHeight": 26,
    "periodSpacing": 6,
    "periodPaddingLeft": 14,
    "periodPaddingRight": 14,
    "periodColor": "#5A5E7C",
    "periodFontSize": 11,
    "periodFontWeight": "medium",
    "periodSelectedFrameColor": "#CFE9EC",
    "periodSelectedColor": "#0B6F68",
    "periodSelectedFontWeight": "semibold",
    "noteColor": "#7A7E98",
    "noteFontSize": 11.5,
    "logoFrameColor": "#FBE9EC",
    "logoWidth": 26,
    "logoCornerRadius": 13,
    "logoColor": "#1FA99C",
    "logoFontSize": 8,
    "logoFontWeight": "bold",
    "fundCodeColor": "#1C1E33",
    "fundCodeFontSize": 13,
    "fundCodeFontWeight": "bold",
    "fundCodeTextAlign": "center",
    "fundNameColor": "#5A5E7C",
    "fundNameFontSize": 10.5,
    "fundNameTextAlign": "center",
    "fundHeaderSpacing": 3,
    "labelColumnWidth": 100,
    "fundColumnWidth": 84,
    "cellPaddingTop": 10,
    "cellPaddingBottom": 10,
    "cellPaddingLeft": 4,
    "cellPaddingRight": 4,
    "gridFrameColor": "#FFFFFF",
    "rowLabelColor": "#3D405C",
    "rowLabelFontSize": 12.5,
    "rowLabelTextAlign": "left",
    "cellColor": "#1C1E33",
    "cellFontSize": 13,
    "cellTextAlign": "center",
    "rowDividerColor": "#EEF0F6",
    "rowDividerHeight": 1,
    "rowHighlightColor": "#E7F8F5",
    "stickyDividerColor": "#1F000000",
    "stickyDividerWidth": 5,
    "bestFrameColor": "#D7E36B",
    "bestCornerRadius": 9,
    "bestSpacing": 3,
    "bestPaddingTop": 4,
    "bestPaddingBottom": 4,
    "bestPaddingLeft": 8,
    "bestPaddingRight": 8,
    "bestColor": "#3B4310",
    "bestFontSize": 10.5,
    "bestFontWeight": "semibold",
    "disclaimerFrameColor": "#FFF6E0",
    "disclaimerCornerRadius": 10,
    "disclaimerPaddingTop": 8,
    "disclaimerPaddingBottom": 8,
    "disclaimerPaddingLeft": 10,
    "disclaimerPaddingRight": 10,
    "disclaimerSpacing": 8,
    "disclaimerIconColor": "#E8A400",
    "disclaimerIconFontSize": 14,
    "disclaimerColor": "#8A5A00",
    "disclaimerFontSize": 12,
    "buttonFrameColor": "#23295A",
    "buttonHeight": 46,
    "buttonCornerRadius": 23,
    "buttonColor": "#FFFFFF",
    "buttonFontSize": 15,
    "buttonFontWeight": "semibold",
    "chartTitleColor": "#1C1E33",
    "chartTitleFontSize": 13,
    "chartTitleFontWeight": "semibold",
    "chartFrameColor": "#F7F8FB",
    "chartCornerRadius": 12,
    "chartHeight": 210,
    "chartPaddingTop": 12,
    "chartPaddingBottom": 10,
    "chartPaddingLeft": 10,
    "chartPaddingRight": 12,
    "chartAxisColor": "#5A5E7C",
    "chartAxisFontSize": 11,
    "chartGridColor": "#D5D9E5",
    "chartGridWidth": 1,
    "chartGridDashLength": 4,
    "chartLineWidth": 2.5,
    "chartPointWidth": 6,
    "chartGuideColor": "#B9BED0",
    "chartGuideWidth": 1,
    "legendFrameColor": "#F7F8FB",
    "legendCornerRadius": 12,
    "legendSpacing": 10,
    "legendPaddingTop": 12,
    "legendPaddingBottom": 12,
    "legendPaddingLeft": 12,
    "legendPaddingRight": 12,
    "legendTitleColor": "#1C1E33",
    "legendTitleFontSize": 13,
    "legendTitleFontWeight": "bold",
    "legendFundColor": "#1C1E33",
    "legendFundFontSize": 13,
    "legendFundFontWeight": "semibold",
    "legendValueColor": "#1C1E33",
    "legendValueFontSize": 14,
    "legendValueFontWeight": "bold",
    "legendDotWidth": 10,
    "legendDividerColor": "#E1E4EE",
    "legendDividerHeight": 1,
    "positiveColor": "#21A84C",
    "negativeColor": "#E5302B"
  }
}`;

/**
 * The menu, in the order it is offered.
 *
 * `label` is what the admin clicks; `cardType` is what `detectCardType` will report once that JSON
 * lands in the box, which is what the row chip shows — so a sample whose `cardType` here disagrees
 * with its own `type` key is a bug you can see. `refId` is likewise the document's own.
 *
 * 🔴 `key` EXISTS BECAUSE THE TWO PICKER ENTRIES SHARE BOTH `cardType` AND `refId`. They are two
 * documents of ONE card type — the picker's two modes — so neither of those identifies a menu row
 * any more, and using one as the `v-for` key would give Vue duplicate keys (and the two rows one
 * `data-testid` between them). `key` is the menu's own identity and nothing else reads it; it
 * matches `refId` for the cards that have no twin, so their test ids are unchanged.
 */
export const CARD_CONFIG_SAMPLES = [
  { key: 'type8', cardType: 'TAX_FUND_FACTSHEET', refId: 'type8', label: 'Fund factsheet (type8)', json: FACTSHEET_SAMPLE },
  { key: 'type9', cardType: 'TAX_FUND_COMPARISON', refId: 'type9', label: 'Fund comparison (type9)', json: COMPARISON_SAMPLE },
  { key: 'type10', cardType: 'TAX_FUND_PERFORMANCE', refId: 'type10', label: 'Fund performance (type10)', json: PERFORMANCE_SAMPLE },
  {
    key: 'type14',
    cardType: 'MF',
    refId: 'type14',
    label: 'Fund picker (type14) — เจอหลายกอง ไม่ได้ขอเทียบ',
    json: PICKER_SAMPLE,
  },
  {
    key: 'type14-compare',
    cardType: 'MF',
    refId: 'type14',
    label: 'Fund picker (type14) — ขอเทียบ เลือกได้หลายกอง',
    json: PICKER_COMPARE_SAMPLE,
  },
  {
    key: 'type15',
    cardType: 'TAX_FUND_COMPARISON_V2',
    refId: 'type15',
    label: 'Fund comparison V2 (type15) — template 4 แท็บ',
    json: COMPARISON_V2_TEMPLATE,
  },
];

export default {
  FACTSHEET_SAMPLE,
  COMPARISON_SAMPLE,
  PERFORMANCE_SAMPLE,
  PICKER_SAMPLE,
  PICKER_COMPARE_SAMPLE,
  COMPARISON_V2_TEMPLATE,
  CARD_CONFIG_SAMPLES,
};
